import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce, find, uniq } from "lodash";
import * as React from "react";
import { FormGroup, Input, Label } from "reactstrap";
import { GrantTypeMap } from "./ClientsTable";
import * as ClientStore from "./ClientStore";

const { Component } = React;

interface ClientScope {
    scope: string;
}

export class Client {
    constructor() {
        this.grantTypes = [];
        this.redirectUris = [];
        this.postLogoutRedirectUris = [];
        this.properties = [];
        this.scopes = [];
    }

    id: string;
    name: string;
    allowOfflineAccess: boolean;
    allowAccessTokensViaBrowser: boolean;
    requireConsent: boolean;
    requireClientSecret: boolean;
    disableContext: boolean;
    grantTypes: Array<string>;
    redirectUris: Array<string>;
    postLogoutRedirectUris: Array<string>;
    backChannelLogoutUri: string;
    properties: Array<KeyValue>;
    scopes: Array<ClientScope>;
    uri: string;
    logoUri: string;
    requirePKCE: boolean;
}

interface KeyValue {
    key: string;
    value: string;
}

interface EditClientProps {
    organization: string;
    updated: Function;
    clientId: string;
}

interface EditClientState {
    client: Client;
    isLoaded: boolean;
    error?: any;
    hasApiAccess: boolean;
    hasRegistrationEnabled: boolean;
    registrationUri: string;
    newRedirectUri: string;
    newPostLogoutRedirectUri: string;
    disableContext: boolean;
}

const API_ACCESSS_KEY = "permissions.identityApi";
const REGISTRATION_ALLOWED_KEY = "registration.enabled";
const REGISTRATION_URI_KEY = "registration.uri";
const DISABLE_CONTEXT = "context.disabled";
const CONFINED_TYPES = "confinedTypes";

export const EditClient = ({ clientId, organization, updated }: EditClientProps) => {
    const [client, setClient] = React.useState(new Client());
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [hasApiAccess, setHasApiAccess] = React.useState(false);
    const [hasRegistrationEnabled, setHasRegistrationEnabled] = React.useState(false);
    const [registrationUri, setRegistrationUri] = React.useState("");
    const [newRedirectUri, setNewRedirectUri] = React.useState("");
    const [newPostLogoutRedirectUri, setNewPostLogoutRedirectUri] = React.useState("");
    const [disableContext, setDisableContext] = React.useState(false);
    const [error, setError] = React.useState<any>(undefined);

    let editClient = (client) => {
        return ClientStore.UpdateClient(client).then(
            (result) => {
                setIsLoaded(true);
                setClient(result.data.client);

                updated && updated(result.data.client);
            },
            (error) => {
                setIsLoaded(true);

                setError(error);

                updated && updated(null);
            }
        );
    };

    const debounceEditClient = React.useCallback(
        debounce((client) => {
            editClient(client);
        }, 500),
        []
    );

    const debounceHandleRegistrationUriChange = React.useCallback(
        debounce((value) => {
            ClientStore.UpdateClientProperties(client.id, REGISTRATION_URI_KEY, value).then((result) => {
                // props.updated(result.client);
            });
        }, 500),
        [client]
    );

    React.useEffect(() => {
        ClientStore.GetClient(clientId).then((res) => {
            setClient(res.data);
            setIsLoaded;
        });
    }, []);

    React.useEffect(() => {
        if (client.id) {
            setHasApiAccess(client.properties.filter((g) => g.key == API_ACCESSS_KEY && g.value == "true").length > 0);
            setHasRegistrationEnabled(client.properties.filter((g) => g.key == REGISTRATION_ALLOWED_KEY && g.value == "true").length > 0);
            setRegistrationUri((find(client.properties, { key: REGISTRATION_URI_KEY }) || {}).value);
            setDisableContext(client.properties.filter((g) => g.key == DISABLE_CONTEXT && g.value == "true").length > 0);
        }
    }, [client]);

    React.useEffect(() => {
        if (client.id) {
            debounceEditClient(client);
        }
    }, [
        client.name,
        client.backChannelLogoutUri,
        client.uri,
        client.logoUri,
        client.allowOfflineAccess,
        client.requireConsent,
        client.requireClientSecret,
        // client.grantTypes,
        // client.redirectUris,
        // client.postLogoutRedirectUris,
        // client.properties,
        // client.scopes,
    ]);

    let handleNameChange = (e) => {
        var name = e.target.value;

        setClient({ ...client, name });
    };

    let handleBackChannelLogoutUriChange = (e) => {
        var backChannelLogoutUri = e.target.value;

        setClient({ ...client, backChannelLogoutUri });
    };

    let handleUriChange = (e) => {
        var uri = e.target.value;
        setClient({ ...client, uri });
    };

    let handleLogoUriChange = (e) => {
        var logoUri = e.target.value;
        setClient({ ...client, logoUri });
    };

    let handleOfflineAccessChange = (e) => {
        var allowOfflineAccess = e.target.checked;
        setClient({ ...client, allowOfflineAccess });
    };

    let handleAllowAccessTokensViaBrowser = (e) => {
        var allowAccessTokensViaBrowser = e.target.checked;
        setClient({ ...client, allowAccessTokensViaBrowser });
    };

    let handleRequireConsentChange = (e) => {
        var requireConsent = e.target.checked;
        setClient({ ...client, requireConsent });
    };

    let handleRequireSecretChange = (e) => {
        var requireClientSecret = e.target.checked;
        setClient({ ...client, requireClientSecret });
    };

    let handleApiAccessChange = (e) => {
        var value = e.target.checked;

        setHasApiAccess(value),
            ClientStore.UpdateClientProperties(client.id, API_ACCESSS_KEY, value).then((result) => {
                updated(result.data.client);
            });
    };

    let handleRegistrationAllowedChange = (e) => {
        var value = e.target.checked;

        setHasRegistrationEnabled(value);

        ClientStore.UpdateClientProperties(client.id, REGISTRATION_ALLOWED_KEY, value).then((result) => {
            updated(result.data.client);
        });
    };

    let handleDisableContextChange = (e) => {
        var value = e.target.checked;

        setDisableContext(value);

        ClientStore.UpdateClientProperties(client.id, DISABLE_CONTEXT, value).then((result) => {
            updated(result.data.client);
        });
    };

    let handleNewRedirectUriChange = (e) => {
        var value = e.target.value;

        setNewRedirectUri(value);
    };

    let handleNewPostLogoutRedirectUriChange = (e) => {
        var value = e.target.value;

        setNewPostLogoutRedirectUri(value);
    };

    let handleRegistrationUriChange = (e) => {
        var value = e.target.value;
        setRegistrationUri(value);
        debounceHandleRegistrationUriChange(value);
    };

    let createRedirectUri = () => {
        ClientStore.CreateClientRedirectUri(client.id, newRedirectUri).then(() => {
            ClientStore.GetClient(client.id).then((result) => {
                setClient(result.data);
                setNewRedirectUri("");
            });
        });
    };

    let removeRedirectUri = (redirectUri) => {
        ClientStore.RemoveClientRedirectUri(client.id, redirectUri).then(() => {
            ClientStore.GetClient(client.id).then((result) => {
                setClient(result.data);
            });
        });
    };

    let createPostLogoutRedirectUri = () => {
        ClientStore.CreateClientPostLogoutRedirectUri(client.id, newPostLogoutRedirectUri).then(() => {
            ClientStore.GetClient(client.id).then((result) => {
                setClient(result.data);
                setNewPostLogoutRedirectUri("");
            });
        });
    };

    let removePostLogoutRedirectUri = (postLogoutRedirectUri) => {
        ClientStore.RemoveClientPostLogoutRedirectUri(client.id, postLogoutRedirectUri).then(() => {
            ClientStore.GetClient(client.id).then((result) => {
                setClient(result.data);
            });
        });
    };

    let addGrantType = (grantType) => () => {
        let grantTypes = client.grantTypes.concat(grantType);
        grantTypes = uniq(grantTypes);
        setClient({ ...client, grantTypes });
        debounceEditClient({ ...client, grantTypes });
    };

    let removeGrantType = (grantType) => () => {
        let grantTypes = client.grantTypes.filter((s) => s != grantType);
        setClient({ ...client, grantTypes });
        debounceEditClient({ ...client, grantTypes });
    };

    let addScope = (scope) => () => {
        let scopes = client.scopes.concat(scope);
        scopes = uniq(scopes);
        setClient({ ...client, scopes });
        setClient({ ...client, scopes });
    };

    let removeScope = (scope) => () => {
        let scopes = client.scopes.filter((s) => s != scope);
        setClient({ ...client, scopes });
        setClient({ ...client, scopes });
    };

    let handleRequirePKCEChange = (e) => {
        var requirePKCE = e.target.checked;

        setClient({ ...client, requirePKCE });
        setClient({ ...client, requirePKCE });
    };

    let addConfinedType = (type) => () => {
        let confinedTypes = find(client.properties, { key: CONFINED_TYPES });

        let updateValue = type;

        if (confinedTypes) {
            updateValue = confinedTypes.value + "," + type;
        }

        ClientStore.UpdateClientProperties(client.id, CONFINED_TYPES, updateValue).then((result) => {
            updated(result.data.client);
            ClientStore.GetClient(clientId).then((res) => {
                setClient(res.data);
                setIsLoaded;
            });
        });
    };

    let removeConfinedType = (type) => () => {
        let confinedTypes = find(client.properties, { key: CONFINED_TYPES });

        let updateValue = null;

        if (confinedTypes) {
            updateValue = confinedTypes.value
                .split(",")
                .filter((s) => s != type)
                .join(",");
        }

        ClientStore.UpdateClientProperties(client.id, CONFINED_TYPES, updateValue).then((result) => {
            updated(result.data.client);
            ClientStore.GetClient(clientId).then((res) => {
                setClient(res.data);
                setIsLoaded;
            });
        });
    };

    const update = () => {
        editClient(client);
    };

    return (
        <>
            {client && (
                <div className="row">
                    <div className="col">
                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Name</label>
                            <div className="col-sm-9 pl-0">
                                <input type="text" className="form-control" value={client.name} onChange={handleNameChange} />
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label"></label>
                            <div className="col-sm-9 pl-0">
                                <FormGroup check>
                                    <Input type="switch" label="" id="offlineAccess" checked={client.allowOfflineAccess} onChange={handleOfflineAccessChange} />
                                    <Label for="offlineAccess" check>
                                        Allow Offline Authentication
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label"></label>
                            <div className="col-sm-9 pl-0">
                                <FormGroup check>
                                    <Input type="switch" label="" id="accessTokensViaBrowser" checked={client.allowAccessTokensViaBrowser} onChange={handleAllowAccessTokensViaBrowser} />
                                    <Label for="accessTokensViaBrowser" check>
                                        Allow Access Tokens Via Browser
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label"></label>
                            <div className="col-sm-9 pl-0">
                                <FormGroup check>
                                    <Input type="switch" label="" id="requireConsent" checked={client.requireConsent} onChange={handleRequireConsentChange} />
                                    <Label for="requireConsent" check>
                                        Require User Consent
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label"></label>
                            <div className="col-sm-9 pl-0">
                                <FormGroup check>
                                    <Input type="switch" label="" id="requireSecret" checked={client.requireClientSecret} onChange={handleRequireSecretChange} />
                                    <Label for="requireSecret" check>
                                        Require User Secret
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label"></label>
                            <div className="col-sm-9 pl-0">
                                <FormGroup check>
                                    <Input type="switch" label="" id="context" checked={disableContext} onChange={handleDisableContextChange} />
                                    <Label for="context" check>
                                        Disable Context for Client
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label"></label>
                            <div className="col-sm-9 pl-0">
                                <FormGroup check>
                                    <Input type="switch" label="" id="apiAccess" checked={hasApiAccess} onChange={handleApiAccessChange} />
                                    <Label for="apiAccess" check>
                                        Admin Role
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label"></label>
                            <div className="col-sm-9 pl-0">
                                <FormGroup check>
                                    <Input type="switch" label="" id="registrationEnabled" checked={hasRegistrationEnabled} onChange={handleRegistrationAllowedChange} />
                                    <Label for="registrationEnabled" check>
                                        Allow customer registration
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label"></label>
                            <div className="col-sm-9 pl-0">
                                <FormGroup check>
                                    <Input type="switch" label="" id="requirePKCE" checked={client.requirePKCE} onChange={handleRequirePKCEChange} />
                                    <Label for="requirePKCE" check>
                                        Require PKCE
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Registration URI</label>
                            <div className="col-sm-9 pl-0">
                                <input type="text" className="form-control" value={registrationUri || ""} onChange={handleRegistrationUriChange} />
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Redirect Uris</label>
                            <div className="col-sm-9 pl-0">
                                {client.redirectUris &&
                                    client.redirectUris.map((r, key) => (
                                        <div key={"redirectUri" + key} className="d-flex justify-content-between">
                                            <span>{r}</span>
                                            <button type="button" className="btn btn-outline-danger" onClick={() => removeRedirectUri(r)}>
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </button>
                                        </div>
                                    ))}
                            </div>
                            <div className="col-12 p-3">
                                <div className="row">
                                    <div className="col-10">
                                        <input type="text" className="form-control" value={newRedirectUri} onChange={handleNewRedirectUriChange} />
                                    </div>
                                    <div className="col-2 d-flex justify-content-end">
                                        <button type="button" className="btn btn-outline-success" onClick={createRedirectUri}>
                                            <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Post Logout Redirect Uris</label>
                            <div className="col-sm-9 pl-0">
                                {client.postLogoutRedirectUris &&
                                    client.postLogoutRedirectUris.map((r, key) => (
                                        <div key={"postLogoutRedirectUri" + key} className="d-flex justify-content-between">
                                            <span>{r}</span>
                                            <button type="button" className="btn btn-outline-danger" onClick={() => removePostLogoutRedirectUri(r)}>
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </button>
                                        </div>
                                    ))}
                            </div>
                            <div className="col-12 p-3">
                                <div className="row">
                                    <div className="col-10">
                                        <input type="text" className="form-control" value={newPostLogoutRedirectUri || ""} onChange={handleNewPostLogoutRedirectUriChange} />
                                    </div>
                                    <div className="col-2 d-flex justify-content-end">
                                        <button type="button" className="btn btn-outline-success" onClick={createPostLogoutRedirectUri}>
                                            <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Back Channel Logout Uri</label>
                            <div className="col-sm-9 pl-0">
                                <input type="text" className="form-control" value={client.backChannelLogoutUri || ""} onChange={handleBackChannelLogoutUriChange} />
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Uri</label>
                            <div className="col-sm-9 pl-0">
                                <input type="text" className="form-control" value={client.uri || ""} onChange={handleUriChange} />
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Logo Uri</label>
                            <div className="col-sm-9 pl-0">
                                <input type="text" className="form-control" value={client.logoUri || ""} onChange={handleLogoUriChange} />
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Grant Types</label>
                            <div className="col-sm-9 pl-0">
                                {client.grantTypes &&
                                    client.grantTypes.map((r, key) => (
                                        <div key={"grantType" + key} className="d-flex justify-content-between">
                                            <span>{GrantTypeMap[r]}</span>
                                            <button type="button" className="btn btn-outline-danger" onClick={removeGrantType(r)}>
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </button>
                                        </div>
                                    ))}
                            </div>
                            <div className="col-12 p-3">
                                <button className="btn btn-link" onClick={addGrantType("implicit")}>
                                    Implicit
                                </button>
                                <button className="btn btn-link" onClick={addGrantType("hybrid")}>
                                    Hybrid
                                </button>
                                <button className="btn btn-link" onClick={addGrantType("authorization_code")}>
                                    Authorization Code
                                </button>
                                <button className="btn btn-link" onClick={addGrantType("client_credentials")}>
                                    Client Credentials
                                </button>
                                <button className="btn btn-link" onClick={addGrantType("urn:ietf:params:oauth:grant-type:device_code")}>
                                    Device Flow
                                </button>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Scopes</label>
                            <div className="col-sm-9 pl-0">
                                {client.scopes &&
                                    client.scopes.map((r, key) => (
                                        <div key={"scopes" + key} className="d-flex justify-content-between">
                                            <span>{r}</span>
                                            <button type="button" className="btn btn-outline-danger" onClick={removeScope(r)}>
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </button>
                                        </div>
                                    ))}
                            </div>
                            <div className="col-12 p-3">
                                <button type="button" className="btn btn-link" onClick={addScope("default")}>
                                    default
                                </button>
                                <button type="button" className="btn btn-link" onClick={addScope("profile")}>
                                    profile
                                </button>
                                <button type="button" className="btn btn-link" onClick={addScope("openid")}>
                                    openid
                                </button>
                                <button type="button" className="btn btn-link" onClick={addScope("identity")}>
                                    identity
                                </button>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-3 col-form-label">Membership Types</label>
                            <div className="col-sm-9 pl-0">
                                {find(client.properties, { key: CONFINED_TYPES }) &&
                                    find(client.properties, { key: CONFINED_TYPES })
                                        .value.split(",")
                                        .map((r, key) => (
                                            <div key={"confinedTypes" + key} className="d-flex justify-content-between">
                                                <span>{r}</span>
                                                <button type="button" className="btn btn-outline-danger" onClick={removeConfinedType(r)}>
                                                    <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                                </button>
                                            </div>
                                        ))}
                            </div>
                            <div className="col-12 p-3">
                                <button type="button" className="btn btn-link" onClick={addConfinedType("Staff")}>
                                    Staff
                                </button>
                                <button type="button" className="btn btn-link" onClick={addConfinedType("Parent")}>
                                    Parent
                                </button>
                                <button type="button" className="btn btn-link" onClick={addConfinedType("Student")}>
                                    Student
                                </button>
                                <button type="button" className="btn btn-link" onClick={addConfinedType("Guest")}>
                                    Guest
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-end flex-column h-100">
                        <div className="mb-1">
                            <button className="btn btn-success float-right" onClick={update}>
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
